/**
 * High-level zero-knowledge vault operations.
 *
 * This module is the public surface the browser uses. It composes KDF,
 * symmetric, and asymmetric primitives into the flows described in
 * SECURITY-ARCHITECTURE.md: registration, unlock, item crypto, and sharing.
 */
import { toBytes, fromBytes, toBase64Url, fromBase64, randomBytes, toHex, type Bytes } from './encoding.js';
import { deriveMasterKey, deriveEncKey, deriveAuthHash } from './kdf.js';
import { sealString, openString, encryptBytes, decryptBytes, wrapKey, unwrapKey, AES_GCM_IV_LENGTH } from './symmetric.js';
import { packIvAndCiphertext, unpackIvAndCiphertext } from './encoding.js';
import {
  generateKeyPair,
  exportPublicKey,
  exportPrivateKeyBytes,
  importPublicKey,
  importPrivateKey,
  wrapItemKeyForRecipient,
  unwrapItemKeyWithPrivateKey,
} from './asym.js';
import type { EncryptedItem, ItemFields, KdfParams } from './types.js';

/** A freshly-generated random 256-bit symmetric vault key. */
export function newSymKey(): Bytes {
  return randomBytes(32);
}

/**
 * Run the full registration derivation from a master password.
 * Returns everything the server needs (minus the master password) plus the
 * in-memory keys the client keeps for the session.
 *
 * IMPORTANT: `masterKey`, `encKey`, `symKey`, and `authHash` must be kept in
 * memory only and cleared on lock/logout.
 */
export interface RegistrationBundle {
  /** Wire payload for POST /auth/register (no secrets, no master password). */
  toServer: {
    salt: string;
    kdfParams: KdfParams;
    authHash: string;
    protectedVaultKey: string;
    publicKey: string;
    protectedPrivateKey: string;
    serverSalt: string;
  };
  /** In-memory keys for the active session. */
  session: {
    masterKey: Bytes;
    encKey: Bytes;
    symKey: Bytes;
  };
}

export async function buildRegistration(
  masterPassword: string,
  kdfParams: KdfParams,
): Promise<RegistrationBundle> {
  const salt = randomBytes(16);
  const serverSalt = randomBytes(16);

  const masterKey = await deriveMasterKey(masterPassword, salt, kdfParams);
  const [encKey, authHash] = await Promise.all([deriveEncKey(masterKey), deriveAuthHash(masterKey)]);

  const symKey = newSymKey();
  const protectedVaultKey = await wrapKey(encKey, symKey);

  const keyPair = await generateKeyPair();
  const publicKey = await exportPublicKey(keyPair.publicKey);
  const privateKeyBytes = await exportPrivateKeyBytes(keyPair.privateKey);
  const protectedPrivateKey = await wrapKey(symKey, privateKeyBytes);

  return {
    toServer: {
      salt: toBase64Url(salt),
      kdfParams,
      authHash: toBase64Url(authHash),
      protectedVaultKey,
      publicKey,
      protectedPrivateKey,
      serverSalt: toBase64Url(serverSalt),
    },
    session: { masterKey, encKey, symKey },
  };
}

/**
 * Unlock flow given the master password and the user's stored KDF params + salt.
 * Returns the in-memory keys and the recovered symKey. Does NOT send anything
 * over the network — the caller separately posts `authHash` for authentication.
 */
export interface UnlockResult {
  masterKey: Bytes;
  encKey: Bytes;
  authHash: Bytes;
  symKey: Bytes;
}

export async function unlock(
  masterPassword: string,
  salt: Bytes,
  kdfParams: KdfParams,
  protectedVaultKey: string,
): Promise<UnlockResult> {
  const masterKey = await deriveMasterKey(masterPassword, salt, kdfParams);
  const [encKey, authHash] = await Promise.all([deriveEncKey(masterKey), deriveAuthHash(masterKey)]);
  const symKey = await unwrapKey(encKey, protectedVaultKey);
  return { masterKey, encKey, authHash, symKey };
}

/** Re-wrap the vault key under a NEW master password (master-password change). */
export async function rewrapForNewMasterPassword(
  newMasterPassword: string,
  salt: Bytes,
  kdfParams: KdfParams,
  currentSymKey: Bytes,
): Promise<{ authHash: string; protectedVaultKey: string }> {
  const masterKey = await deriveMasterKey(newMasterPassword, salt, kdfParams);
  const [encKey, authHash] = await Promise.all([deriveEncKey(masterKey), deriveAuthHash(masterKey)]);
  const protectedVaultKey = await wrapKey(encKey, currentSymKey);
  return { authHash: toBase64Url(authHash), protectedVaultKey };
}

// ---------- Item encryption ----------

/** Encrypt a vault item's fields under the user's symKey, producing server-storable blobs. */
export async function encryptItem(symKey: Bytes, fields: ItemFields): Promise<EncryptedItem> {
  const itemKey = randomBytes(32);
  const { iv, ciphertext } = await encryptBytes(itemKey, toBytes(JSON.stringify(fields)));
  const protectedItemKey = await wrapKey(symKey, itemKey);
  return {
    ciphertext: toBase64Url(ciphertext),
    iv: toBase64Url(iv),
    protectedItemKey,
  };
}

/** Decrypt a vault item's fields using the user's symKey. */
export async function decryptItem(symKey: Bytes, enc: EncryptedItem): Promise<ItemFields> {
  const itemKey = await unwrapKey(symKey, enc.protectedItemKey);
  const plaintext = await decryptBytes(itemKey, fromBase64(enc.iv), fromBase64(enc.ciphertext));
  return JSON.parse(fromBytes(plaintext)) as ItemFields;
}

// ---------- Private key load/store (for sharing) ----------

/** Decrypt the stored private key into a usable CryptoKey for the session. */
export async function loadPrivateKey(symKey: Bytes, protectedPrivateKey: string): Promise<CryptoKey> {
  const pkcs8 = await unwrapKey(symKey, protectedPrivateKey);
  return importPrivateKey(pkcs8);
}

/** Wrap an item key for a recipient (lookup their public key, then RSA-OAEP). */
export async function shareItemKey(recipientPublicKeyB64: string, itemKey: Bytes): Promise<string> {
  const recipientPub = await importPublicKey(recipientPublicKeyB64);
  const shared = await wrapItemKeyForRecipient(recipientPub, itemKey);
  return toBase64Url(shared);
}

/** Recover the shared item key from a recipient's perspective. */
export async function receiveSharedItemKey(privateKey: CryptoKey, sharedB64: string): Promise<Bytes> {
  const shared = fromBase64(sharedB64);
  return unwrapItemKeyWithPrivateKey(privateKey, shared);
}

/** Unwrap a single item's key with symKey (needed before re-sharing that item). */
export async function unwrapItemKey(symKey: Bytes, protectedItemKey: string): Promise<Bytes> {
  return unwrapKey(symKey, protectedItemKey);
}

// ---------- Non-secret local digests (security dashboard) ----------

/**
 * A non-reversible local digest of a decrypted password, used ONLY in memory to
 * detect reuse. It is never transmitted. Uses a fixed label so identical
 * passwords across users would still differ per-user (salt = user's symKey);
 * within one vault, identical passwords produce identical digests.
 */
export async function reuseDigest(symKey: Bytes, password: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new Uint8Array([...symKey, ...toBytes(password)]));
  return toHex(new Uint8Array(buf));
}

// re-export low-level pieces callers may need
export { sealString, openString, packIvAndCiphertext, unpackIvAndCiphertext, AES_GCM_IV_LENGTH };
